import React, { useState } from 'react'
import {ImagePlus, Loader2} from 'lucide-react'
import toast from 'react-hot-toast'
import { useSiteEdit } from '@/contexts/SiteEditContext'
import { useUpload } from '@/hooks/useUpload'

interface EditableImageProps {
  /** Cle unique du bloc image (ex: hero.background) */
  contentKey: string
  /** URL d'origine (fallback) */
  src: string
  alt?: string
  section?: string
  label?: string
  className?: string
}

/**
 * Image editable en direct.
 * - Admin + mode edition : overlay pour televerser une nouvelle image.
 */
const EditableImage: React.FC<EditableImageProps> = ({
  contentKey,
  src,
  alt = '',
  section,
  label,
  className = '',
}) => {
  const { get, save, editMode, canEdit } = useSiteEdit()
  const { upload, uploading } = useUpload()
  const [localBusy, setLocalBusy] = useState(false)
  const value = get(contentKey, src)
  const active = canEdit && editMode

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLocalBusy(true)
    try {
      const url = await upload(file)
      if (url) {
        await save(contentKey, url, { type: 'image', section, label: label || contentKey })
      }
    } catch (error) {
      console.error('Echec du televersement:', error)
      toast.error('Échec du téléversement de l\'image.')
    } finally {
      setLocalBusy(false)
      e.target.value = ''
    }
  }

  if (!active) {
    return <img src={value} alt={alt} className={className} />
  }

  const busy = uploading || localBusy

  return (
    <div className="relative group">
      <img src={value} alt={alt} className={className} />
      <label className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#0E1B2C]/70 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-[inherit] outline-dashed outline-2 outline-[#A3C644]/60">
        {busy ? (
          <Loader2 className="w-7 h-7 text-[#A3C644] animate-spin" />
        ) : (
          <>
            <ImagePlus className="w-7 h-7 text-[#A3C644]" />
            <span className="text-[#E8DCC4] text-xs font-medium tracking-wide">
              Changer l'image
            </span>
          </>
        )}
        <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={busy} />
      </label>
    </div>
  )
}

export default EditableImage
